import type { ReactNode } from 'react';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { IssuesByGroup } from '../api/types';

const useStyles = makeStyles(theme => ({
  footerRow: {
    backgroundColor: theme.palette.type === 'dark' ? '#333' : '#f5f5f5',
    position: 'sticky',
    bottom: 0,
    '& td': {
      fontWeight: 'bold',
      borderTop: `2px solid ${theme.palette.divider}`,
      padding: '2px 8px',
    },
  },
  container: {
    maxHeight: 600,
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#fafafa',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: '2px 8px',
    textAlign: 'center',
    whiteSpace: 'pre-line',
  },
  bodyCell: {
    padding: '2px 8px',
  },
}));

const COLUMN_LABELS: Record<string, string> = {
  business_unit: 'Business Unit',
  experience_slug: 'Experience',
  team: 'Team',
  license: 'License',
  total_by_experience: 'Total',
  total_by_team: 'Total',
  total_by_license: 'Total',
  total_by_issue_type: 'Total',
  issue_type: 'Tool',
  total_by_cve: 'Total',
  total_by_pillar: 'Total',
  total_by_category: 'Total',
  total_by_system: 'Total',
  total_by_deployed_instance: 'Total',
  system: 'System',
  deployment_instance: 'Deployed Instance',
  level: 'Level',
  pillar: 'Pillar',
  category: 'Category',
  cve_or_rule: 'CVE / Rule',
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  informational: 'Informational',
  unknown: 'Unknown',
  none: 'None',
};

function formatColumnTitle(key: string): string {
  return COLUMN_LABELS[key.toLowerCase()] ?? key;
}

export function exportTableCsv(
  data: IssuesByGroup[],
  totals: IssuesByGroup,
  columns: string[],
  filename: string,
  cellFormatter?: (col: string, value: number, row: IssuesByGroup) => string,
  titleFormatter: (key: string) => string = formatColumnTitle,
) {
  const header = columns
    .map(c => titleFormatter(c).replace(/\n/g, ' '))
    .join(',');
  const rows = [...data, totals].map(row =>
    columns
      .map(col => {
        const val = row[col] ?? '';
        const str =
          cellFormatter && typeof val === 'number'
            ? cellFormatter(col, val, row)
            : String(val);
        return `"${str.replace(/"/g, '""')}"`;
      })
      .join(','),
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface SummaryTableProps {
  data: IssuesByGroup[];
  nameKey: string;
  exportFilename?: string;
  onRowClick?: (row: IssuesByGroup) => void;
  selectedRowValue?: string | null;
  /** Optional formatter for numeric cell display. Receives (column, value, row) and returns display text. */
  formatCell?: (col: string, value: number, row: IssuesByGroup) => string;
  /** Optional column header overrides keyed by lowercase column name. Falls back to default labels. */
  columnLabels?: Record<string, string>;
  /** Optional override for CSV export. When provided, the Export CSV button calls this instead of the default export. */
  onExportCsv?: () => void;
  /**
   * Optional custom renderer for the name column cells (`nameKey` column).
   * Receives the raw cell value (matches `IssuesByGroup`'s `string | number`
   * union) and whether the row has an `onRowClick` handler — pass this
   * through to any inner link's own `stopPropagation` prop so only the link
   * itself blocks the row click, not the whole cell (see call site below).
   */
  renderNameCell?: (
    value: string | number,
    stopPropagation: boolean,
  ) => ReactNode;
}

type SortDirection = 'asc' | 'desc';

function computeTotals(data: IssuesByGroup[], nameKey: string): IssuesByGroup {
  const totals: IssuesByGroup = { [nameKey]: 'TOTALS' };
  for (const row of data) {
    for (const [key, value] of Object.entries(row)) {
      if (key === nameKey) continue;
      if (typeof value === 'number') {
        totals[key] = ((totals[key] as number) || 0) + value;
      }
    }
  }
  return totals;
}

export const SummaryTable = ({
  data,
  nameKey,
  exportFilename,
  onRowClick,
  selectedRowValue,
  formatCell,
  columnLabels,
  onExportCsv,
  renderNameCell,
}: SummaryTableProps) => {
  const classes = useStyles();
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [order, setOrder] = useState<SortDirection>('desc');

  const colTitle = (key: string): string =>
    columnLabels?.[key.toLowerCase()] ?? formatColumnTitle(key);

  const displayNumeric = (col: string, value: number, row: IssuesByGroup) =>
    formatCell ? formatCell(col, value, row) : value.toLocaleString();

  if (data.length === 0) return null;

  const columns = Object.keys(data[0]);
  const totals = computeTotals(data, nameKey);
  const numericCols = new Set(
    columns.filter(col => typeof data[0][col] === 'number'),
  );

  const handleSort = (column: string) => {
    if (orderBy === column) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(column);
      setOrder('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!orderBy) return 0;
    const aVal = a[orderBy];
    const bVal = b[orderBy];
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const aStr = String(aVal ?? '');
    const bStr = String(bVal ?? '');
    return order === 'asc'
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  });

  return (
    <>
      {exportFilename && (
        <Box mb={1} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            onClick={
              onExportCsv ??
              (() =>
                exportTableCsv(
                  data,
                  totals,
                  columns,
                  exportFilename,
                  formatCell,
                  colTitle,
                ))
            }
          >
            Export CSV
          </Button>
        </Box>
      )}
      <TableContainer component={Paper} className={classes.container}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell
                  key={col}
                  className={classes.headerCell}
                  style={
                    numericCols.has(col) && !columnLabels?.[col.toLowerCase()]
                      ? { textAlign: 'right' }
                      : undefined
                  }
                >
                  <TableSortLabel
                    active={orderBy === col}
                    direction={orderBy === col ? order : 'desc'}
                    onClick={() => handleSort(col)}
                  >
                    {colTitle(col)}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, i) => {
              const rowValue = String(row[nameKey] ?? i);
              const isSelected = selectedRowValue === rowValue;
              const handleClick = () => onRowClick?.(row);
              const handleKeyDown = (e: KeyboardEvent) => {
                if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onRowClick(row);
                }
              };

              return (
                <TableRow
                  key={rowValue}
                  onClick={handleClick}
                  onKeyDown={handleKeyDown}
                  tabIndex={onRowClick ? 0 : undefined}
                  selected={isSelected}
                  aria-selected={isSelected || undefined}
                  style={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    backgroundColor: isSelected
                      ? 'rgba(0, 0, 0, 0.04)'
                      : undefined,
                  }}
                >
                  {columns.map(col => (
                    <TableCell
                      key={col}
                      className={classes.bodyCell}
                      style={
                        numericCols.has(col)
                          ? { textAlign: 'right' }
                          : undefined
                      }
                    >
                      {(() => {
                        if (typeof row[col] === 'number')
                          return displayNumeric(col, row[col] as number, row);
                        if (col === nameKey && renderNameCell) {
                          const rendered = renderNameCell(
                            row[col],
                            !!onRowClick,
                          );
                          // Click bubbles through so long non-link text still
                          // triggers the row (see stopPropagation param above);
                          // but keydown is stopped so Enter/Space activating a
                          // focused link inside the cell (e.g. the CVE/NVD
                          // link) doesn't also bubble to the row's onKeyDown,
                          // which would preventDefault() the link's own
                          // activation and fire onRowClick instead.
                          return onRowClick ? (
                            <span
                              role="presentation"
                              onKeyDown={e => e.stopPropagation()}
                            >
                              {rendered}
                            </span>
                          ) : (
                            rendered
                          );
                        }
                        return row[col];
                      })()}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow className={classes.footerRow}>
              {columns.map(col => (
                <TableCell
                  key={col}
                  style={
                    numericCols.has(col) ? { textAlign: 'right' } : undefined
                  }
                >
                  {typeof totals[col] === 'number'
                    ? displayNumeric(col, totals[col] as number, totals)
                    : totals[col]}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        </MuiTable>
      </TableContainer>
    </>
  );
};
